export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

export async function api<T>(path: string, init: RequestInit & { json?: unknown } = {}): Promise<T> {
  const { json, headers, ...rest } = init;
  const h: Record<string, string> = { accept: 'application/json', ...((headers as Record<string, string> | undefined) ?? {}) };
  let body = rest.body;
  if (json !== undefined) {
    h['content-type'] = 'application/json';
    body = JSON.stringify(json);
  }
  const res = await fetch(path, { ...rest, headers: h, body, credentials: 'same-origin' });
  if (res.status === 204) return undefined as T;
  const data: unknown = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg = (data as { error?: string }).error;
    throw new ApiError(res.status, msg ?? res.statusText);
  }
  return data as T;
}

export type Role = 'owner' | 'analyst' | 'viewer' | 'client';
export type Tenant = { id: string; slug: string; name: string; kind: string };
export type Me = {
  user: { id: string; email: string; display_name: string; must_change_password: boolean };
  tenant: Tenant;
  role: Role;
  /** Set for role = client: the one profile this login is bound to. */
  client: { id: string; name: string } | null;
  tenants: (Tenant & { role: Role })[];
};

export type IngestRun = {
  source_id: string; started_at: string; finished_at: string | null; status: string;
  fetched: number; inserted: number; updated: number; unchanged: number; error: string | null;
  params?: Record<string, unknown> | null;
};

export type TagRef = { tag_id: string; confidence: number };
export type Tag = { id: string; label: string; domain: string; open_notices: number };

export type Decision = 'pending' | 'relevant' | 'pursue' | 'watch' | 'pass';
export type Rationale = {
  tags: { tag_id: string; confidence: number; points: number }[];
  naics: { notice: string | null; match: 'exact' | 'sector' | null; points: number };
  agency: { matched: string | null; points: number };
  set_aside: { notice: string | null; status: 'none' | 'eligible' | 'ineligible'; points: number };
  notice_type: { type: string; points: number };
  title_pattern?: { dla_item: boolean; points: number };
  keywords?: { phrase: string; points: number }[];
};
export type Match = {
  id: string; score: number | null; decision: Decision; rationale: Rationale; decided_at: string | null; created_at: string;
  client_id: string; client_name: string;
  solicitation_id: string; title: string; notice_type: string; solicitation_number: string | null; issuing_org_path: string | null;
  set_aside: string | null; naics: string | null; channel: string; url: string | null; posted_at: string | null; response_due_at: string | null; is_open: boolean; tags: TagRef[];
  is_new: boolean; is_updated: boolean; has_assessment: boolean;
};
export type Assessment = { why: string; implication: string; gaps: string; next_steps: string };
export type AssessmentSource = 'rules' | 'ai' | 'analyst';
export type ClientUpdate = {
  kind: string; target_type: string | null; target_id: string | null; title: string; at: string; match_id: string | null; decision: Decision | null; detail: string | null; is_new: boolean;
};
export type ClientInitiative = {
  match_id: string; score: number | null; decision: Decision; program_id: string; pe_number: string | null; line_item: string | null; title: string;
  service: string | null; ba_title: string | null; appropriation: string | null; latest: { fiscal_year: number; stage: string; amount: number | null } | null;
};
export type OpportunityDetail = Match & {
  psc: string | null; archive_at: string | null; active: boolean; description_url: string | null; place_of_performance: unknown; points_of_contact: unknown;
  first_seen_at: string; last_seen_at: string;
  assessment: Assessment | null; assessment_source: AssessmentSource | null; assessment_model: string | null; assessed_at: string | null; edited_by: string | null;
  client: { id: string; name: string; sector: string | null; status: ClientStatus; capabilities: string[]; technology_tags: string[]; target_agencies: string[]; target_programs: string[]; monitor_keywords: string[]; last_reviewed_at: string | null };
  versions: SolicitationVersion[];
  notes: (Note & { target_type: 'match' | 'solicitation' })[];
  decisions: { changed_at: string; decision: Decision; actor: string | null }[];
  other_clients: { id: string; score: number | null; decision: Decision; client_id: string; client_name: string }[];
};

export type Alert = {
  id: string; kind: string; severity: 'info' | 'notable' | 'material'; title: string; body: string | null;
  target_type: string | null; target_id: string | null; fired_at: string; acknowledged_at?: string | null;
  client_profile_id: string | null; client_name?: string | null;
};

export type Stats = {
  solicitations_active: number; due_14d: number; clients: number; alerts_open: number; mission_needs: number;
  pending_matches: number; pursuing: number;
  last_ingest: IngestRun | null;
  activity: { table_name: string; action: string; changed_at: string; label: string | null }[];
  top_matches: Match[];
  alerts: Alert[];
};

export type Solicitation = {
  id: string; title: string; solicitation_number: string | null; notice_type: string; channel: string;
  issuing_org_path: string | null; naics: string | null; psc: string | null; set_aside: string | null;
  posted_at: string | null; response_due_at: string | null; archive_at: string | null; active: boolean; is_open: boolean;
  url: string | null; first_seen_at: string; last_seen_at: string; versions: number; tags: TagRef[]; notes: number;
  fit: number | null; fit_decision: Decision | null; match_id: string | null;
};
export type SolicitationStatus = 'open' | 'closed' | 'all';
export type SolicitationSort = 'due_asc' | 'due_desc' | 'posted' | 'title' | 'fit';
export type SolicitationList = {
  total: number; limit: number; offset: number; status: SolicitationStatus; sort: SolicitationSort; client: string | null;
  summary: { open: number; closed: number; due_14d: number; amended: number; noted: number };
  items: Solicitation[];
};
export type Note = { id: string; kind: string; body: string; visibility: string; created_at: string; author?: string | null };
export type SolicitationVersion = { id: number; content_hash: string; changed_fields: string[]; observed_at: string };
export type SolicitationMatch = { id: string; score: number | null; decision: Decision; rationale: Rationale; decided_at: string | null; client_id: string; client_name: string };
export type SolicitationDetail = Omit<Solicitation, 'versions' | 'notes' | 'fit' | 'fit_decision' | 'match_id'> & {
  description_url: string | null;
  place_of_performance: unknown; points_of_contact: unknown;
  versions: SolicitationVersion[]; notes: Note[]; matches: SolicitationMatch[];
};

export type ClientStatus = 'prospect' | 'active' | 'inactive';
export type ClientProfile = {
  id: string; name: string; sector: string | null; size: string | null; status: ClientStatus;
  technology_tags: string[]; capabilities: string[]; target_agencies: string[]; set_asides: string[]; updated_at: string; last_reviewed_at: string | null;
  notes: number; pending_matches: number; new_matches: number; priority: number; pursuing: number; top_score: number | null; alerts_open: number;
};
export type ClientDetail = {
  id: string; name: string; sector: string | null; size: string | null; status: ClientStatus; summary: string | null; government_work: string | null;
  capabilities: string[]; technology_tags: string[]; naics_codes: string[]; target_agencies: string[]; target_programs: string[]; markets: string[];
  contract_vehicles: string[]; certifications: string[]; set_asides: string[]; strategic_priorities: string | null; bd_objectives: string | null;
  opportunity_types: string[]; funding_interests: string[]; monitor_keywords: string[]; partnership_interests: string | null; relationships: string | null;
  last_reviewed_at: string | null; last_reviewed_by_name: string | null;
  created_at: string; updated_at: string;
  counts: { pending: number; new: number; relevant: number; pursue: number; watch: number; pass: number; alerts_open: number; notes: number };
  matches: Match[]; new_opportunities: Match[]; priority: Match[]; updates: ClientUpdate[]; initiatives: ClientInitiative[]; alerts: Alert[]; notes: Note[];
  history: { action: string; changed_at: string; actor: string | null }[];
  needs: { match_id: string; score: number | null; role: string | null; decision: string; need_id: string; title: string; status: string; sponsor_name: string | null; mission_area: string | null }[];
  events: { match_id: string; score: number | null; decision: Decision; rationale: { action: string; why: string }; event_id: string; name: string; kind: string; starts_on: string | null; city: string | null; state: string | null; next_deadline: string | null }[];
};
export type ProfileInput = {
  name: string; sector?: string; size?: string; summary?: string; government_work?: string;
  capabilities?: string[]; technology_tags?: string[]; naics_codes?: string[]; target_agencies?: string[]; target_programs?: string[]; markets?: string[];
  contract_vehicles?: string[]; certifications?: string[]; set_asides?: string[];
  strategic_priorities?: string; bd_objectives?: string;
  opportunity_types?: string[]; funding_interests?: string[]; monitor_keywords?: string[]; partnership_interests?: string; relationships?: string;
  status?: ClientStatus;
};

export type ProgramLine = {
  id: string; pe_number: string | null; line_item: string | null; title: string; appropriation: string | null; account_code: string | null;
  service: string | null; organization: string | null; budget_activity: string | null; ba_title: string | null; exhibit: 'r1' | 'p1';
  py_actual: number | null; cy_enacted: number | null; by_request: number | null; by_total: number | null;
  mark: { stage: string; amount: number | null } | null; tags: TagRef[]; notes: number;
  fit: number | null; fit_decision: Decision | null; match_id: string | null;
};
export type ProgramList = {
  budget_year: number; current_year: number; prior_year: number; sort: string; client: string | null; limit: number; offset: number;
  summary: { total: number; by_total: number | null; by_request: number | null; cy_enacted: number | null; py_actual: number | null; marked: number; new_starts: number; fitted: number };
  items: ProgramLine[];
};
export type TotalsCheck = { header: string; fiscal_year: number; stage: string; kind: 'amount' | 'quantity'; sheet_total: number | null; computed: number; included: number; matches: boolean };
export type BudgetDocument = {
  id: number; fiscal_year: number; exhibit: string; stage: string; title: string; filename: string | null; source_url: string | null; uploaded_at: string;
  rows_seen: number; lines_created: number; versions_new: number; versions_same: number; byte_size: number | null; totals: TotalsCheck[] | null; uploaded_by: string | null;
};
export type BudgetChange = {
  id: number; fiscal_year: number; stage: string; amount_thousands: number | null; prev: number | null; observed_at: string; source_id: string;
  program_id: string; pe_number: string | null; line_item: string | null; title: string; service: string | null;
};
export type BudgetSummary = {
  budget_year: number; years: number[]; documents: BudgetDocument[];
  by_exhibit: { exhibit: 'r1' | 'p1'; lines: number; by_total: number | null; by_request: number | null; cy_enacted: number | null; py_actual: number | null; new_starts: number; marked: number }[];
  increases: ProgramLine[]; decreases: ProgramLine[]; matched_lines: number; changes: BudgetChange[];
};
export type BudgetVersion = {
  id: number; fiscal_year: number; stage: string; amount_thousands: number | null; quantity: number | null; source_id: string;
  source_document: string | null; source_locator: string | null; observed_at: string; notes: string | null; document_title: string | null; recorded_by: string | null;
};
export type ProgramDetail = {
  id: string; pe_number: string | null; line_item: string | null; title: string; appropriation: string | null; account_code: string | null;
  service: string | null; organization: string | null; budget_activity: string | null; ba_title: string | null; created_at: string; updated_at: string;
  versions: BudgetVersion[]; tags: TagRef[]; notes: Note[];
  matches: { id: string; score: number | null; decision: Decision; rationale: { tags?: { tag_id: string; points: number }[]; capabilities?: { phrase: string; points: number }[]; agency?: { matched: string | null; points: number } }; client_id: string; client_name: string }[];
};
export type IngestReport = {
  already_loaded: boolean; document_id: number; exhibit: string; budget_year: number; filename: string;
  rows_seen: number; rows_skipped: number; lines: number; programs_created: number; programs_updated: number;
  versions_new: number; versions_same: number; tags: number; totals: TotalsCheck[]; totals_ok: boolean; warnings: string[];
};

// ---- Phase 2: mission needs, ecosystem, events ------------------------------------------------
export type NeedStatus = 'signal' | 'validated' | 'matched' | 'engaged' | 'solicited' | 'closed';
export type NeedDecision = 'pending' | 'shortlist' | 'team' | 'pass';
export type TeamRole = 'provider' | 'integrator' | 'research' | 'manufacturing' | 'test' | 'convener' | 'sponsor';
export type MissionNeed = {
  id: string; title: string; sponsor_name: string | null; sponsor_org_name?: string | null; mission_area: string | null;
  technology_tags: string[]; required_capabilities: string[]; status: NeedStatus; preferred_geography: string | null; target_date: string | null; updated_at: string;
  candidates: number; team: number; links: number; notes: number;
};
export type NeedRationale = {
  tags: { tag_id: string; points: number }[];
  required_covered: { phrase: string; via: string; points: number }[];
  text_hits: { phrase: string; points: number }[];
  maturity: { required: number | null; candidate: number | null; points: number };
  geography: { preferred: string | null; candidate: string | null; points: number };
  role: TeamRole;
};
export type NeedCandidate = {
  id: string; candidate_type: 'client_profile' | 'organization'; candidate_id: string; name: string; kind: string | null; state: string | null; city: string | null;
  is_client: boolean; score: number | null; role: TeamRole | null; decision: NeedDecision; rationale: NeedRationale; decided_at: string | null;
};
export type TeamFormation = {
  required: { capability: string; covered_by: { candidate_id: string; name: string; decision: string; score: number | null }[]; gap: boolean }[];
  suggested_team: { role: TeamRole; candidate: { candidate_id: string; name: string; score: number | null; decision: string } | null }[];
  gaps: string[];
};
export type NeedLink = { id: string; target_type: string; target_id: string; relation: string | null; created_at: string; label: string | null };
export type NeedDetail = Omit<MissionNeed, 'candidates' | 'team' | 'links' | 'notes'> & {
  government_org_id: string | null; problem: string | null; desired_outcome: string | null; technical_constraints: string | null;
  maturity_required: string | null; funding_pathways: string | null; source: string | null; created_at: string;
  candidates: NeedCandidate[]; team: TeamFormation;
  related_solicitations: { id: string; title: string; notice_type: string; issuing_org_path: string | null; response_due_at: string | null; is_open: boolean; tags: TagRef[]; overlap: number }[];
  related_programs: { id: string; pe_number: string | null; line_item: string | null; title: string; service: string | null; ba_title: string | null; overlap: number; latest: { fiscal_year: number; amount: number | null } | null }[];
  links: NeedLink[]; notes: Note[];
};
export type NeedInput = {
  title: string; sponsor_name?: string; mission_area?: string; problem?: string; desired_outcome?: string; technology_tags?: string[]; required_capabilities?: string[];
  technical_constraints?: string; maturity_required?: string; funding_pathways?: string; preferred_geography?: string; target_date?: string | null; status?: NeedStatus; source?: string;
};

export type OrgKind = 'government' | 'company' | 'university' | 'lab' | 'consortium' | 'nonprofit' | 'other';
export type Organization = {
  id: string; name: string; kind: OrgKind; city: string | null; state: string | null; country: string | null; website: string | null; is_prime: boolean; tags: string[]; description: string | null;
  claims: number; claim_tags: string[]; roles: string[]; trl: number | null; client_profile_id: string | null; need_matches: number;
};
export type Claim = { id: string; capability: string; technology_tags: string[]; trl: number | null; roles: string[]; maturity: string | null; facilities: string | null; geography: string | null; evidence: string | null; created_at: string; author: string | null };
export type OrganizationDetail = Omit<Organization, 'claims' | 'claim_tags' | 'roles' | 'trl' | 'client_profile_id' | 'need_matches'> & {
  uei: string | null; cage: string | null; created_at: string; claims: Claim[];
  needs: { match_id: string; score: number | null; role: TeamRole | null; decision: NeedDecision; need_id: string; title: string; status: NeedStatus }[];
  clients: { id: string; name: string; status: string }[]; notes: Note[];
};
export type EcosystemSummary = { organizations: number; with_claims: number; claims: number; by_kind: Record<string, number> | null; by_state: Record<string, number> | null };

export type EventKind = 'conference' | 'industry_day' | 'proposers_day' | 'tim' | 'workshop' | 'symposium' | 'association' | 'commercial' | 'investor' | 'convening' | 'other';
export type EventAction = 'Attend' | 'Exhibit' | 'Submit' | 'Speak' | 'Team' | 'Monitor';
export type EventRationale = { tags: { tag_id: string; points: number }[]; agency: { matched: string | null; points: number }; kind: { kind: string; points: number }; capability_hits: { phrase: string; points: number }[]; action: EventAction; also: EventAction[]; why: string };
export type EventRow = {
  id: string; name: string; kind: EventKind; status: string; description: string | null; starts_on: string | null; ends_on: string | null; city: string | null; state: string | null; url: string | null;
  organizer_org_id: string | null; organizer_name: string | null; registration_opens_on: string | null; registration_closes_on: string | null; abstract_due_on: string | null;
  speaker_due_on: string | null; exhibitor_due_on: string | null; created_at: string; tags: string[]; next_deadline: string | null; recommended_for: number;
  fit: number | null; fit_decision: Decision | null; match_id: string | null; recommendation: EventRationale | null;
};
export type EventDetail = EventRow & { recommendations: { id: string; score: number | null; decision: Decision; rationale: EventRationale; client_id: string; client_name: string }[]; notes: Note[] };
export type EventInput = {
  name: string; kind: EventKind; starts_on?: string | null; ends_on?: string | null; city?: string; state?: string; url?: string; description?: string;
  registration_opens_on?: string | null; registration_closes_on?: string | null; abstract_due_on?: string | null; speaker_due_on?: string | null; exhibitor_due_on?: string | null; status?: string; tags?: string[];
};

export type AdminUser = {
  id: string; email: string; display_name: string; role: Role; disabled_at: string | null; must_change_password: boolean;
  created_at: string; member_since: string; workspaces: number; client_profile_id: string | null; client_name: string | null;
};
